/**
 * /teacher-dashboard — Scholar Athlete Academy "Flight Control".
 * Teacher-only command center: Daily Flight Plan (Claude, CA-standard aligned),
 * live station-rotation navigation, real-time station telemetry, and the
 * exit-ticket mastery board.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Radio, LogOut, Loader2, RefreshCw, Compass, GraduationCap,
  CircleDot, CheckCircle2, AlertTriangle, Clock, Target, Users, Play, Pause, Send,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import TeacherDayPlanner from '../components/TeacherDayPlanner';
import TeacherHub from '../components/TeacherHub';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const GROUP_META = {
  g5: { label: 'Grade 5', color: 'text-sky-300', ring: 'border-sky-500', bg: 'bg-sky-500/10' },
  g67: { label: 'Grade 6/7', color: 'text-amber-300', ring: 'border-amber-500', bg: 'bg-amber-500/10' },
  g8: { label: 'Grade 8', color: 'text-emerald-300', ring: 'border-emerald-500', bg: 'bg-emerald-500/10' },
};
const STATUS_META = {
  working: { label: 'Working', icon: CircleDot, cls: 'text-sky-400' },
  stuck: { label: 'Stuck — needs help', icon: AlertTriangle, cls: 'text-red-400' },
  completed: { label: 'Module complete', icon: CheckCircle2, cls: 'text-emerald-400' },
};

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [plan, setPlan] = useState(null);
  const [live, setLive] = useState(null);
  const [stationData, setStationData] = useState(null);
  const [mastery, setMastery] = useState(null);
  const [inSession, setInSession] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [prep, setPrep] = useState(null);
  const [prepBusy, setPrepBusy] = useState(null);
  const pollRef = useRef(null);

  const loadPrep = useCallback(async () => {
    try {
      const r = await api.get('/academy/summer-prep', { headers: authHeaders() });
      setPrep(r.data);
    } catch (e) { /* keep last */ }
  }, []);

  const loadPlan = useCallback(async (force = false) => {
    const r = await api.get(`/academy/flight-plan/today${force ? '?force=true' : ''}`, { headers: authHeaders() });
    setPlan(r.data.plan);
  }, []);

  const loadLive = useCallback(async () => {
    try {
      const [ls, ss, mv] = await Promise.all([
        api.get('/academy/flight-plan/live-state', { headers: authHeaders() }),
        api.get('/academy/flight-plan/station-status', { headers: authHeaders() }),
        api.get('/academy/flight-plan/mastery', { headers: authHeaders() }),
      ]);
      setLive(ls.data.state);
      setStationData(ss.data);
      setMastery(mv.data);
    } catch (e) { /* keep last */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/academy/auth/me', { headers: authHeaders() });
        const user = meRes.data.user || meRes.data;
        if (user.role !== 'teacher' && !user.is_admin && !user.admin_role) {
          toast.error('Teacher access required');
          navigate('/academy/login', { replace: true });
          return;
        }
        setMe(user);
        await Promise.all([loadPlan(), loadLive(), loadPrep()]);
      } catch (e) {
        navigate('/academy/login', { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, loadPlan, loadLive, loadPrep]);
  // Poll live telemetry while In-Session
  useEffect(() => {
    if (inSession) {
      loadLive();
      pollRef.current = setInterval(loadLive, 20000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
    }
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [inSession, loadLive]);

  const regenerate = async () => {
    setRegenerating(true);
    try { await loadPlan(true); toast.success('Game plan regenerated'); }
    catch { toast.error('Could not regenerate'); }
    finally { setRegenerating(false); }
  };

  const [sendingPreviews, setSendingPreviews] = useState(false);
  const emailParents = async () => {
    setSendingPreviews(true);
    try {
      const r = await api.post('/academy/flight-plan/send-parent-previews', {}, { headers: authHeaders() });
      toast.success(`Sent to ${r.data.sent} parent${r.data.sent === 1 ? '' : 's'}`);
    } catch { toast.error('Could not send previews'); }
    finally { setSendingPreviews(false); }
  };

  const regeneratePrep = async (grade) => {
    setPrepBusy(grade);
    try {
      await api.post(`/academy/summer-prep/${grade}/regenerate`, {}, { headers: authHeaders() });
      await loadPrep();
      toast.success(`Grade ${grade} summer plan regenerated`);
    } catch { toast.error('Could not regenerate'); }
    finally { setPrepBusy(null); }
  };

  const logout = () => {
    localStorage.removeItem('academy_token');
    localStorage.removeItem('academy_user');
    navigate('/academy/login');
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>;
  }

  const groups = plan?.groups || {};
  const teacherGroupKey = live?.teacher_group_key;
  const stations = live?.stations;
  const phaseLabel = {
    pre_session: 'Pregame', in_block: `In-Game · Block ${live?.current_block}`,
    transition: 'Timeout · Between Blocks', dismissed: 'Post-Game',
  }[live?.phase] || '—';

  // Map students by group for station 2/3 status
  const studentsByStation = { 2: [], 3: [] };
  (stationData?.students || []).forEach((s) => {
    if (s.current_station === 2) studentsByStation[2].push(s);
    else if (s.current_station === 3) studentsByStation[3].push(s);
  });

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="teacher-dashboard">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-black/60 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logos/saa-athlete-mark.png" alt="Scholar Athlete Academy" className="h-12 w-auto object-contain" />
            <div>
              <div className="text-[9px] tracking-[0.4em] uppercase text-emerald-400 font-black">Scholar Athlete Academy</div>
              <h1 className="text-lg font-black uppercase tracking-tight leading-none">Game Control</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setInSession((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2.5 text-[11px] font-black tracking-[0.2em] uppercase transition-colors border-2 ${inSession ? 'bg-emerald-500 border-emerald-500 text-black' : 'border-zinc-700 text-zinc-300 hover:border-emerald-500'}`}
              data-testid="in-session-toggle"
            >
              {inSession ? <><Radio className="w-4 h-4 animate-pulse" /> In-Game · Live</> : <><Play className="w-4 h-4" /> Kickoff</>}
            </button>
            <button onClick={logout} className="p-2.5 border-2 border-zinc-800 text-zinc-500 hover:text-white hover:border-red-500 transition-colors" data-testid="teacher-logout" title="Sign out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-8 space-y-10">
        {/* New gamified hub: header stats, week-at-a-glance, today's lessons by grade */}
        <TeacherHub />

        {/* Daily briefing header */}
        <div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-zinc-500 font-black">
                {plan?.weekday} · {plan?.date} · Focus: {plan?.subject}
              </div>
              <h2 className="mt-2 text-2xl sm:text-3xl font-black leading-tight max-w-3xl" data-testid="daily-theme">
                {plan?.daily_theme}
              </h2>
              <div className="text-[10px] text-zinc-600 mt-2 uppercase tracking-widest">
                {plan?.generated_by === 'claude' ? 'AI-generated · CA-standard aligned' : 'Template plan'}
              </div>
            </div>
            <button onClick={regenerate} disabled={regenerating} className="flex items-center gap-2 px-4 py-2 border-2 border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-white text-[10px] font-black tracking-[0.2em] uppercase transition-colors" data-testid="regenerate-plan">
              {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Regenerate
            </button>
          </div>
        </div>
        <div className="mt-3">
          <button onClick={emailParents} disabled={sendingPreviews} className="flex items-center gap-2 px-4 py-2 border-2 border-amber-600 text-amber-300 hover:bg-amber-500 hover:text-black text-[10px] font-black tracking-[0.2em] uppercase transition-colors" data-testid="email-parents">
            {sendingPreviews ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Email Parents Today's Plan
          </button>
        </div>

        {/* LIVE BLOCK NAVIGATION */}
        {inSession && (
          <div className="border-2 border-emerald-500/40 bg-emerald-500/5 p-5" data-testid="live-block-panel">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <div className="flex items-center gap-2 text-emerald-400">
                <Radio className="w-4 h-4 animate-pulse" />
                <span className="text-xs font-black tracking-[0.2em] uppercase">{phaseLabel}</span>
                <span className="text-zinc-500 text-xs font-mono">· {live?.now} PT</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500">
                <Clock className="w-3.5 h-3.5" />
                {(live?.blocks || []).map((b) => (
                  <span key={b.block} className={`px-2 py-0.5 border ${live?.current_block === b.block ? 'border-emerald-500 text-emerald-300' : 'border-zinc-800'}`}>
                    B{b.block} {b.start}-{b.end}
                  </span>
                ))}
                <span className="px-2 py-0.5 border border-amber-600 text-amber-400">Exit {live?.exit_ticket_time}</span>
              </div>
            </div>

            {stations ? (
              <div className="grid md:grid-cols-3 gap-3">
                {stations.map((st) => {
                  const gm = GROUP_META[st.group_key] || {};
                  const isTeacher = st.station === 1;
                  const roster = studentsByStation[st.station] || [];
                  return (
                    <div key={st.station} className={`border-2 p-4 ${isTeacher ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-800 bg-black/30'}`} data-testid={`station-${st.station}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-black tracking-[0.2em] uppercase text-zinc-400">Station {st.station}</div>
                        {isTeacher && <span className="text-[9px] font-black bg-emerald-500 text-black px-2 py-0.5 uppercase tracking-widest">You</span>}
                      </div>
                      <div className="text-xs font-bold mt-1 text-zinc-300">{st.name}</div>
                      <div className={`mt-3 text-lg font-black ${gm.color}`}>{st.group_label}</div>

                      {isTeacher && groups[st.group_key] && (
                        <div className="mt-3 border-t border-emerald-500/30 pt-3">
                          <div className="text-[10px] text-emerald-400 font-black tracking-widest uppercase">{groups[st.group_key].standard_code}</div>
                          <p className="text-[11px] text-zinc-300 mt-1 leading-relaxed">{groups[st.group_key].mini_lesson}</p>
                        </div>
                      )}

                      {!isTeacher && (
                        <div className="mt-3 border-t border-zinc-800 pt-3 space-y-1.5" data-testid={`station-${st.station}-roster`}>
                          {roster.length === 0 ? (
                            <div className="text-[10px] text-zinc-600 italic">No live check-ins yet</div>
                          ) : roster.map((s) => {
                            const sm = STATUS_META[s.status] || STATUS_META.working;
                            const Icon = sm.icon;
                            return (
                              <div key={s.student_id} className="flex items-center justify-between text-[11px]">
                                <span className="text-zinc-300 truncate">{s.name}</span>
                                <span className={`flex items-center gap-1 ${sm.cls} font-bold`}><Icon className="w-3.5 h-3.5" /> {sm.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-zinc-500 text-sm">
                {live?.phase === 'dismissed' ? 'The academic day is complete. Rotation resumes at Block 1 (12:00 PT).' : 'Between blocks — passing period. Next block starts shortly.'}
              </div>
            )}

            {stationData?.counts && (
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-zinc-800 text-[11px] font-bold">
                <span className="flex items-center gap-1.5 text-sky-400"><CircleDot className="w-3.5 h-3.5" /> {stationData.counts.working} working</span>
                <span className="flex items-center gap-1.5 text-red-400"><AlertTriangle className="w-3.5 h-3.5" /> {stationData.counts.stuck} stuck</span>
                <span className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> {stationData.counts.completed} complete</span>
              </div>
            )}
          </div>
        )}

        {/* DAILY FLIGHT PLAN — 3 grade groups */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <GraduationCap className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-black tracking-[0.2em] uppercase">Daily Game Plan · Direct Instruction</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {['g5', 'g67', 'g8'].map((gk) => {
              const g = groups[gk];
              const gm = GROUP_META[gk];
              const isTeacher = inSession && teacherGroupKey === gk;
              const promptLabel = { g5: 'Checking for Understanding', g67: 'Guided Practice', g8: 'Advanced Application' }[gk];
              return (
                <div key={gk} className={`border-2 ${isTeacher ? 'border-emerald-500' : gm.ring} ${gm.bg} p-4 flex flex-col`} data-testid={`plan-group-${gk}`}>
                  <div className="flex items-center justify-between">
                    <div className={`text-lg font-black ${gm.color}`}>{gm.label}</div>
                    {isTeacher && <span className="text-[9px] font-black bg-emerald-500 text-black px-2 py-0.5 uppercase tracking-widest animate-pulse">At Your Station</span>}
                  </div>
                  {g ? (
                    <>
                      <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-black tracking-widest uppercase text-zinc-400">
                        <Target className="w-3 h-3" /> {g.standard_code}
                      </div>
                      <p className="text-[11px] text-zinc-500 mt-1 leading-snug">{g.standard_text}</p>
                      <div className="mt-3 border-t border-zinc-700/60 pt-3">
                        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Mini-Lesson</div>
                        <p className="text-xs text-zinc-200 leading-relaxed">{g.mini_lesson}</p>
                      </div>
                      <div className="mt-3 border-t border-zinc-700/60 pt-3 flex-1">
                        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-2">{promptLabel} Prompts</div>
                        <ol className="space-y-2">
                          {(g.prompts || []).map((p, i) => (
                            <li key={i} className="text-xs text-zinc-300 flex gap-2">
                              <span className={`font-black ${gm.color}`}>{i + 1}.</span>
                              <span className="leading-snug">{p}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </>
                  ) : <div className="text-xs text-zinc-600 mt-4">No plan for this group.</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* MASTERY BOARD */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-black tracking-[0.2em] uppercase">Exit Ticket · Mastery Board</h3>
            </div>
            {mastery?.summary && (
              <div className="text-[11px] font-bold flex gap-4">
                <span className="text-emerald-400">{mastery.summary.passed} pass</span>
                <span className="text-amber-400">{mastery.summary.review} review</span>
                <span className="text-zinc-500">{mastery.summary.total} submitted</span>
              </div>
            )}
          </div>
          <div className="border-2 border-zinc-800 overflow-hidden" data-testid="mastery-board">
            <div className="grid grid-cols-12 bg-zinc-900 text-[9px] font-black tracking-[0.15em] uppercase text-zinc-500 px-4 py-2.5">
              <div className="col-span-4">Student</div>
              <div className="col-span-2">Grade</div>
              <div className="col-span-3">CA Standard</div>
              <div className="col-span-3 text-right">Status</div>
            </div>
            {(!mastery?.rows || mastery.rows.length === 0) ? (
              <div className="px-4 py-8 text-center text-zinc-600 text-sm">No exit tickets submitted yet. They aggregate live at {live?.exit_ticket_time || '2:55'}.</div>
            ) : mastery.rows.map((r) => (
              <div key={r.student_id} className="grid grid-cols-12 items-center px-4 py-3 border-t border-zinc-800 text-sm" data-testid={`mastery-row-${r.student_id}`}>
                <div className="col-span-4 font-bold text-zinc-200 truncate">{r.name}</div>
                <div className="col-span-2 text-zinc-400">Grade {r.grade}</div>
                <div className="col-span-3 text-zinc-400 font-mono text-xs">{r.standard_code || '—'}</div>
                <div className="col-span-3 text-right">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest ${r.status === 'pass' ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {r.status === 'pass' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {r.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* LESSON PLANNER — day navigator + per-grade worksheet content */}
        <div>
          <TeacherDayPlanner />
        </div>

        {/* SUMMER PREP PLANS — implementable pre-Aug-24 summer work per grade */}
        <div data-testid="summer-prep-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-black tracking-[0.2em] uppercase">Summer Prep Plans · Before Aug 24</h3>
            </div>
            {prep?.countdown && (
              <div className="text-[11px] font-bold text-zinc-500">
                {prep.countdown.has_started
                  ? 'School year in session'
                  : <><span className="text-emerald-400">{prep.countdown.days_until} days</span> to first day · {prep.countdown.start_label}</>}
              </div>
            )}
          </div>
          <p className="text-[11px] text-zinc-500 mb-4 max-w-2xl leading-relaxed">
            Each enrolled student is auto-assigned a self-paced review bridge from the grade below. Use these grade plans to build the summer work you assign your students.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {(prep?.plans || []).map((p) => (
              <div key={p.grade} className="border-2 border-zinc-800 p-4" data-testid={`summer-prep-grade-${p.grade}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-black text-white">Grade {p.grade}</div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-black">Reviews Grade {p.review_grade}</div>
                  </div>
                  <button
                    onClick={() => regeneratePrep(p.grade)}
                    disabled={prepBusy === p.grade}
                    className="flex items-center gap-1.5 px-3 py-1.5 border-2 border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-white text-[9px] font-black tracking-[0.2em] uppercase transition-colors"
                    data-testid={`regenerate-prep-${p.grade}`}
                  >
                    {prepBusy === p.grade ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Regen
                  </button>
                </div>
                <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{p.overview}</p>
                {Array.isArray(p.weekly_plan) && (
                  <div className="grid grid-cols-2 gap-1.5 mt-3">
                    {p.weekly_plan.map((w) => (
                      <div key={w.week} className="border border-zinc-800 px-2 py-1.5">
                        <div className="text-[9px] font-black uppercase tracking-widest text-emerald-400">Wk {w.week}</div>
                        <div className="text-[10px] text-zinc-300 leading-tight">{w.theme}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 space-y-1.5">
                  {(p.subjects || []).map((s, i) => (
                    <details key={i} className="border-t border-zinc-800 pt-2">
                      <summary className="text-[11px] font-bold text-zinc-300 cursor-pointer flex items-center justify-between">
                        <span>{s.subject}</span>
                        <span className="text-[10px] text-zinc-500 font-normal truncate ml-2 max-w-[55%]">{s.focus}</span>
                      </summary>
                      <ul className="mt-1.5 space-y-1 pl-1">
                        {(s.activities || []).map((a, j) => (
                          <li key={j} className="text-[11px] text-zinc-400 leading-snug flex gap-1.5">
                            <span className="text-emerald-400 font-black shrink-0">{j + 1}.</span>{a}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;
